/**
 * Mirroring the builder's robot into the AR scene, live.
 *
 * The 8th Wall pipeline owns its own three.js scene whose camera is driven by
 * SLAM; the builder's scene is owned by react-three-fiber and keeps being
 * updated from the server over the websocket. An Object3D cannot sit in both,
 * so the AR scene holds lightweight clones that share the originals' geometry
 * and materials, and every frame their transforms are copied across. That is
 * what makes the overlay live: drag a joint in the sheet and the robot in the
 * room moves with it.
 */

import * as THREE from "three";
import { ROBOT_MESH_FLAG } from "../scene/arQuickLook";

function isRobotMesh(object: THREE.Object3D): boolean {
  for (let node: THREE.Object3D | null = object; node; node = node.parent) {
    if (node.userData?.[ROBOT_MESH_FLAG]) {
      return true;
    }
  }
  return false;
}

/**
 * Where this mesh belongs, from the nearest ancestor that says.
 *
 * A GLB module is a group carrying the link and instance with its
 * sub-meshes underneath, so the mesh itself often knows nothing — and a
 * clone that knows nothing cannot answer a tap with a joint.
 */
export function robotPartOf(object: THREE.Object3D): { instanceId: string | null; linkName: string | null } {
  for (let node: THREE.Object3D | null = object; node; node = node.parent) {
    const linkName = node.userData?.linkName ?? null;
    const instanceId = node.userData?.instanceId ?? null;
    if (linkName || instanceId) {
      return { instanceId, linkName };
    }
  }
  return { instanceId: null, linkName: null };
}

/** The Z-up-to-Y-up turn: the builder's up axis onto the AR scene's. */
const Z_UP_TO_Y_UP = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

/**
 * Create the group the mirrored robot lives in.
 *
 * The builder is Z-up and the AR scene is Y-up with the floor at y=0, so the
 * whole mirror is rotated once here — the same convention as the USDZ export.
 */
export function createMirrorRoot(): THREE.Group {
  const root = new THREE.Group();
  root.quaternion.copy(Z_UP_TO_Y_UP);
  return root;
}

/**
 * Stand the mirror on a surface the engine recognised.
 *
 * The hit carries the surface's pose, whose +Y is its normal; composing it
 * with the Z-up correction is the whole trick — the robot's own up axis then
 * runs along whatever the surface's normal is. A floor hit has an identity
 * rotation, so this degenerates to the old position-only placement; a wall
 * lays the base flat against itself; a ramp tilts the robot with it.
 */
export function placeMirrorOnSurface(
  root: THREE.Group,
  position: { x: number; y: number; z: number },
  rotation?: { x: number; y: number; z: number; w: number }
): void {
  root.position.set(position.x, position.y, position.z);
  const surface = rotation
    ? new THREE.Quaternion(rotation.x, rotation.y, rotation.z, rotation.w)
    : new THREE.Quaternion();
  root.quaternion.copy(surface).multiply(Z_UP_TO_Y_UP);
}

/**
 * Bring the mirror up to date with the source scene.
 *
 * Clones are keyed by the source mesh's uuid: meshes that appeared since the
 * last sync get a clone, meshes that are gone lose theirs, and every clone
 * takes its source's world transform and current material. Materials are
 * reassigned each sync because selection and collision swap the material
 * object itself rather than mutating it.
 */
export function syncMirror(
  source: THREE.Object3D,
  mirrorRoot: THREE.Group,
  clones: Map<string, THREE.Mesh>
): void {
  source.updateMatrixWorld(true);

  const seen = new Set<string>();
  source.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh || !isRobotMesh(object)) {
      return;
    }
    seen.add(mesh.uuid);

    let clone = clones.get(mesh.uuid);
    if (!clone) {
      clone = new THREE.Mesh(mesh.geometry, mesh.material);
      // Transforms are written straight into the matrix; letting three
      // recompose it from position/quaternion would overwrite them.
      clone.matrixAutoUpdate = false;
      // Where this part belongs, so a tap on it in the room can find the
      // joint that poses it — read from the ancestors, because a GLB
      // module keeps it on the group above its sub-meshes.
      clone.userData = robotPartOf(mesh);
      clones.set(mesh.uuid, clone);
      mirrorRoot.add(clone);
    }

    clone.geometry = mesh.geometry;
    clone.material = mesh.material;
    clone.matrix.copy(mesh.matrixWorld);
  });

  for (const [uuid, clone] of clones) {
    if (!seen.has(uuid)) {
      mirrorRoot.remove(clone);
      clones.delete(uuid);
      // Geometry and material belong to the source mesh, so nothing is
      // disposed here.
    }
  }
}
